import { BaseI18n } from '@i18n-micro/core'
import { Env } from '~/types/environment'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

// Outside Release an empty translation renders its key, as nuxt2 did
// (plugins/i18n-empty-fallback.ts), so QA can see which strings are blank.
// On Release it renders blank: Lokalise can't tell a value waiting for
// translation from one a market never needs, so production shows neither the
// key nor English (AB#64990). A missing key already renders the key.
// setMissingHandler can't help here: i18n-micro ignores what the handler
// returns, and it never calls the handler for an empty string.

// The unpatched t/tc and the flag, kept once on the shared prototype. Every
// load of this module wraps the saved originals rather than an earlier wrapper,
// so the patch can't stack when the module is evaluated again (vitest with
// isolate: false reloads plugins but not @i18n-micro/core; AB#65207).
const STATE = Symbol.for('su.i18n-empty-fallback')

interface PatchState {
  t: BaseI18n['t']
  tc: BaseI18n['tc']
  showKeys: boolean
}

const proto = BaseI18n.prototype as BaseI18n & { [STATE]?: PatchState }
const state = proto[STATE] ??= { t: proto.t, tc: proto.tc, showKeys: false }

export const setShowEmptyTranslationKeys = (env: Env | undefined) => {
  state.showKeys = (env ?? Env.Release) !== Env.Release
}

// resolveLookup is protected in BaseI18n's types; it is the lookup t/tc use.
const isEmptyTranslation = (i18n: BaseI18n, key: unknown) =>
  typeof key === 'string' && (i18n as unknown as { resolveLookup: (key: string) => unknown }).resolveLookup(key) === ''

function withEmptyHandling<A extends [unknown, ...unknown[]], R> (translate: (this: BaseI18n, ...args: A) => R) {
  return function (this: BaseI18n, ...args: A): R {
    const value = translate.apply(this, args)
    const [key] = args
    // t returns '' for an empty translation but tc returns the key, so either
    // result needs the lookup to tell an empty translation apart.
    if ((value !== '' && value !== key) || !isEmptyTranslation(this, key)) {
      return value
    }
    return (state.showKeys ? key : '') as R
  }
}

// nuxt-i18n-micro binds $t/$tc to its instance during its own plugin setup,
// so the prototype is patched here, when the plugin modules load, before any
// plugin runs.
BaseI18n.prototype.t = withEmptyHandling(state.t)
BaseI18n.prototype.tc = withEmptyHandling(state.tc)

export const syncEmptyTranslationKeys = () => {
  setShowEmptyTranslationKeys(useTypedRuntimeConfig().public.ENVIRONMENT)
}

export default defineNuxtPlugin({
  name: 'i18n-empty-fallback',
  setup: syncEmptyTranslationKeys
})
